import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { isTauri } from '@tauri-apps/api/core';
import { useGetInstances } from 'gen/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';

import CustomDialog from '@ui/Components/Modals/CustomDialog';
import { UIInstance } from '@ui/Types/Address';
import { DeviceInfo, getDeviceInfo } from '@ui/Utils/device/renkiInvoke';

import GetFormInput from '../../Form/GetFormInput';
import { DiscoveryPhase } from '../types';

interface DeviceDiscoveryModalProps {
    discoveredName?: string;
    error?: string | null;
    onCancel: () => void;
    onClose: () => void;
    onStart: (instance: UIInstance | null) => void;
    onSubmit: (payload: { description: string; location: boolean; name: string; visible: boolean }) => void;
    open: boolean;
    phase: DiscoveryPhase;
    submitting: boolean;
}

export default function DeviceDiscoveryModal({
    discoveredName,
    error,
    onCancel,
    onClose,
    onStart,
    onSubmit,
    open,
    phase,
    submitting,
}: Readonly<DeviceDiscoveryModalProps>) {
    const { data: instances = [] } = useGetInstances();
    const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(() => instances[0]?.id || null);
    const tauri = isTauri();

    // Update selected instance when instances load
    useEffect(() => {
        if (instances.length > 0 && !selectedInstanceId) {
            setSelectedInstanceId(instances[0]?.id || null);
        }
    }, [instances, selectedInstanceId]);

    const { control, handleSubmit, setValue } = useForm({
        defaultValues: {
            description: '',
            location: true,
            name: discoveredName ?? '',
            visible: true,
        },
    });

    useEffect(() => {
        if (discoveredName) {
            setValue('name', discoveredName);
        }
    }, [discoveredName, setValue]);

    // Fetch device info only in desktop (tauri) environment; keep simple on web
    const enableDeviceInfo = tauri && phase === DiscoveryPhase.Found;
    const { data: deviceInfo } = useSWR<DeviceInfo>(
        enableDeviceInfo ? '_/deviceInfo' : null,
        async () => {
            const info = await getDeviceInfo();
            return info;
        },
        {
            dedupingInterval: 10_000,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        },
    );

    const renderContent = () => {
        if (phase === DiscoveryPhase.Prompt) {
            return (
                <Stack spacing={2}>
                    <Typography variant="body2">A new device may be available. Start automatic discovery?</Typography>
                    {instances.length > 0 && (
                        <FormControl fullWidth size="small">
                            <InputLabel>Instance</InputLabel>
                            <Select
                                label="Instance"
                                onChange={(e) => setSelectedInstanceId(e.target.value)}
                                value={selectedInstanceId || ''}
                            >
                                {instances.map((instance) => (
                                    <MenuItem key={instance.id} value={instance.id}>
                                        {instance.name || instance.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Typography
                        sx={{
                            color: 'text.secondary',
                        }}
                        variant="caption"
                    >
                        You can also start a scan later from the Devices view if you dismiss now.
                    </Typography>
                </Stack>
            );
        }
        if (phase === DiscoveryPhase.Scanning) {
            return (
                <Stack
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                        textAlign: 'center',
                    }}
                >
                    <CircularProgress size={32} />
                    <Box>
                        <Typography variant="subtitle2">Scanning device…</Typography>
                        {!tauri && <Typography variant="subtitle2">This may take couple of minutes.</Typography>}
                    </Box>
                    <Button onClick={onCancel} size="small" variant="text">
                        Cancel
                    </Button>
                </Stack>
            );
        }
        if (phase === DiscoveryPhase.Found) {
            const desktop = deviceInfo && deviceInfo.type === 'desktop' ? deviceInfo : null;
            const mobile = deviceInfo && deviceInfo.type === 'mobile' ? deviceInfo : null;

            const desktopDetails = desktop ? (
                <>
                    <Typography variant="body2">
                        <strong>CPU:</strong> {desktop.cpu || '—'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Model:</strong> {desktop.model || '—'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Name:</strong> {desktop.name || '—'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Product:</strong> {desktop.product || '—'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>OS:</strong> {desktop.os || '—'}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Ram:</strong> {desktop.ram || '—'}
                    </Typography>
                </>
            ) : null;

            const mobileDetails = mobile ? (
                <>
                    <Typography variant="body2">
                        <strong>Device:</strong> {mobile.device}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Model:</strong> {mobile.model}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Manufacturer:</strong> {mobile.manufacturer}
                    </Typography>
                </>
            ) : null;
            return (
                <form
                    onSubmit={handleSubmit((values) => {
                        onSubmit({
                            description: values.description,
                            location: values.location,
                            name: values.name,
                            visible: values.visible,
                        });
                    })}
                >
                    <Stack spacing={2}>
                        {/* Left: Status + Raw summary */}
                        <Stack spacing={2} sx={{ minWidth: 220 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: 'success.main',
                                        fontWeight: 600,
                                    }}
                                    variant="h6"
                                >
                                    ✓ New device found!
                                </Typography>
                            </Stack>
                            {deviceInfo ? (
                                <Paper sx={{ bgcolor: '#EDF2F6', p: 2 }} variant="outlined">
                                    <Stack spacing={0.5}>
                                        {desktopDetails}
                                        {mobileDetails}
                                    </Stack>
                                </Paper>
                            ) : null}
                        </Stack>
                        {/* Middle: Form fields */}
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                                variant="h6"
                            >
                                Device Information
                            </Typography>
                            <GetFormInput
                                control={control}
                                helperText="Enter the name of the device"
                                label="Name"
                                required
                                type="string"
                                value="name"
                            />
                            <GetFormInput
                                control={control}
                                helperText="Provide a brief description of the device"
                                label="Description"
                                type="string"
                                value="description"
                            />
                        </Stack>
                        {/* Right: Settings */}
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                                variant="h6"
                            >
                                Device Settings
                            </Typography>
                            <Paper sx={{ p: 2 }} variant="outlined">
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography
                                            gutterBottom
                                            sx={{
                                                fontWeight: 500,
                                            }}
                                            variant="subtitle2"
                                        >
                                            Enable visibility
                                        </Typography>
                                        <GetFormInput
                                            control={control}
                                            helperText="This device can be tracked within system"
                                            label="Visible"
                                            type="boolean"
                                            value="visible"
                                        />
                                    </Box>
                                    <Divider />
                                    <Box>
                                        <Typography
                                            gutterBottom
                                            sx={{
                                                fontWeight: 500,
                                            }}
                                            variant="subtitle2"
                                        >
                                            Enable location
                                        </Typography>
                                        <GetFormInput
                                            control={control}
                                            helperText="Display device location within system"
                                            label="Location sharing"
                                            type="boolean"
                                            value="location"
                                        />
                                    </Box>
                                </Stack>
                            </Paper>
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Button onClick={onClose} size="small" type="button" variant="text">
                                    Cancel
                                </Button>
                                <Button disabled={submitting} size="small" type="submit" variant="contained">
                                    {submitting ? 'Saving…' : 'Create'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </form>
            );
        }
        if (phase === DiscoveryPhase.Error) {
            return (
                <Stack spacing={1}>
                    <Typography color="error">{error || 'Discovery failed.'}</Typography>
                    <Button
                        onClick={() => {
                            const selectedInstance = instances.find((inst) => inst.id === selectedInstanceId);
                            onStart(selectedInstance || null);
                        }}
                        size="small"
                        variant="contained"
                    >
                        Retry
                    </Button>
                    <Button onClick={onClose} size="small" variant="text">
                        Close
                    </Button>
                </Stack>
            );
        }
        if (phase === DiscoveryPhase.Completed) {
            return <Typography>Device added successfully.</Typography>;
        }
        return null;
    };

    const shouldOpen = open && phase !== DiscoveryPhase.Idle && phase !== DiscoveryPhase.Cancelled;

    return (
        <CustomDialog
            cancelLabel={phase === DiscoveryPhase.Prompt ? 'Dismiss' : 'Cancel'}
            confirmLabel={phase === DiscoveryPhase.Prompt ? 'Start' : 'Close'}
            content={{
                body: <Box sx={{ width: '100%' }}>{renderContent()}</Box>,
                title: 'Add Device',
            }}
            disableBackdropDismiss
            hideActions={
                phase === DiscoveryPhase.Scanning || phase === DiscoveryPhase.Found || phase === DiscoveryPhase.Error
            }
            hideCancel={phase === DiscoveryPhase.Found || phase === DiscoveryPhase.Completed}
            onClose={onClose}
            onConfirm={() => {
                if (phase === DiscoveryPhase.Prompt) {
                    const selectedInstance = instances.find((inst) => inst.id === selectedInstanceId);
                    onStart(selectedInstance || null);
                } else if (phase === DiscoveryPhase.Completed) {
                    onClose();
                }
            }}
            open={shouldOpen}
        />
    );
}
